import { ReactNode } from "react";
import { useUser } from "@/hooks/use-user.ts";
import AcceptTermsModal from "@/features/Terms/AcceptTermsModal.tsx";
import { useOptionalSelectedWorkspace } from "@/hooks/use-selected-workspace.ts";

/**
 * Whether the blocking terms-acceptance modal is up. Only org admins/owners
 * are prompted, since only they can accept on the organization's behalf.
 * Other dialogs wait for it rather than open on top of it.
 */
export function useTermsAcceptancePending(): boolean {
    const user = useUser();
    const selectedWorkspace = useOptionalSelectedWorkspace();

    return (
        !!selectedWorkspace &&
        user.getCurrentOrganization().needsTermsAcceptance() &&
        user.hasAdminPermissions() &&
        !user?.managesOrganization(selectedWorkspace.organization) &&
        !user.isSuperAdmin()
    );
}

/**
 * Shows a blocking terms-acceptance modal over the app when the current
 * organization has not accepted the latest terms version.
 */
export default function TermsAcceptanceGuard({
    children,
}: {
    children: ReactNode;
}) {
    const user = useUser();
    const organization = user.getCurrentOrganization();
    const shouldPrompt = useTermsAcceptancePending();

    return (
        <>
            {children}
            {shouldPrompt && (
                <AcceptTermsModal version={organization.currentTermsVersion} />
            )}
        </>
    );
}
